import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Check, Close } from '@mui/icons-material';
import { useMutation, useQuery } from '@apollo/client';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { APPROVE_LEAVE, GET_LEAVE_REQUESTS, REJECT_LEAVE } from '../graphql/queries';
import { useAuth } from '../context/AuthContext';

const formatDate = (isoDate: string) =>
  new Date(isoDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const LeaveManagement: React.FC = () => {
  const { user } = useAuth();
  const organizationId = user?.organization?.id;

  const { data, loading, error, refetch } = useQuery(GET_LEAVE_REQUESTS, {
    variables: { organizationId, status: null },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
  });

  const [approveLeave, { loading: approving }] = useMutation(APPROVE_LEAVE);
  const [rejectLeave, { loading: rejecting }] = useMutation(REJECT_LEAVE);

  const leaveRequests = data?.leaveRequests ?? [];

  const handleApprove = async (id: string) => {
    await approveLeave({ variables: { id, reviewNotes: 'Approved by manager' } });
    await refetch();
  };

  const handleReject = async (id: string) => {
    await rejectLeave({ variables: { id, reviewNotes: 'Rejected by manager' } });
    await refetch();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Sidebar />
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Header />
        <Box component="main" sx={{ flex: 1, p: 3 }}>
          <Container maxWidth="xl">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                Leave Management
              </Typography>
              <Chip label="Live Data" color="success" variant="outlined" />
            </Box>

            {!organizationId && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are not attached to an organization.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Could not load leave requests.
              </Alert>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card sx={{ borderRadius: 3 }}>
                  <CardContent>
                    {loading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={30} />
                      </Box>
                    ) : (
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Employee</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Start Date</TableCell>
                              <TableCell>End Date</TableCell>
                              <TableCell>Reason</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {leaveRequests.map((request: any) => (
                              <TableRow key={request.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Avatar sx={{ bgcolor: '#E91E63', mr: 2, width: 32, height: 32 }}>
                                      {request.employee?.name?.charAt(0) || '?'}
                                    </Avatar>
                                    {request.employee?.name || 'Unknown'}
                                  </Box>
                                </TableCell>
                                <TableCell>{request.leaveType}</TableCell>
                                <TableCell>{formatDate(request.startDate)}</TableCell>
                                <TableCell>{formatDate(request.endDate)}</TableCell>
                                <TableCell>{request.reason || '-'}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={request.status.toLowerCase()}
                                    size="small"
                                    color={
                                      request.status === 'APPROVED'
                                        ? 'success'
                                        : request.status === 'REJECTED'
                                        ? 'error'
                                        : 'warning'
                                    }
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {request.status === 'PENDING' && (
                                    <>
                                      <Tooltip title="Approve request">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="success"
                                            disabled={approving || rejecting}
                                            onClick={() => handleApprove(request.id)}
                                          >
                                            <Check />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Reject request">
                                        <span>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            disabled={approving || rejecting}
                                            onClick={() => handleReject(request.id)}
                                          >
                                            <Close />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                            {leaveRequests.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  No leave requests found.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default LeaveManagement;
